import { Badge, Box, Callout, Card, Flex, Grid, Heading, Progress, ScrollArea, Separator, Text } from "@radix-ui/themes";
import { useMemo } from "react";
import { useGetDashboardStats, useGetWeeklySummary } from "hooks";
import { ChartCard } from "components";
import { ReviewsByRatingChart } from "components/charts/ReviewsByRatingChart";
import { SentimentTrendChart } from "../../components/charts/SentimentTrendChart";
import { StackedStatusBar } from "components/charts/StackedStatusBar";
import {
  RATING_OPTIONS,
  SENTIMENT_OPTIONS,
  useDateRangeContext,
  useLocationContext,
  useProfileContext,
  useReviewsFilterContext,
} from "context";

const formatISODate = isoDate => {
  if (!isoDate || typeof isoDate !== "string") return "";
  const parts = isoDate.split("-");
  return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : isoDate;
};

function ThemeColumn({ sentiment, themes }) {
  const positive = sentiment === "positive";
  return (
    <Flex direction="column" gap="3" minWidth="0">
      <Text size="2" weight="bold" color={positive ? "green" : "red"} style={{ letterSpacing: ".09em", textTransform: "uppercase" }}>
        {sentiment} themes
      </Text>
      {themes.length ? themes.map((theme, index) => (
        <Box key={theme.name}>
          <Flex align="center" justify="between" gap="3" py="3">
            <Flex align="center" gap="2" minWidth="0" wrap="wrap">
              <Text weight="medium">{theme.name}</Text>
              <Badge color={positive ? "green" : "red"} variant="soft" radius="full">{sentiment}</Badge>
            </Flex>
            <Text size="2" color="gray" style={{ whiteSpace: "nowrap" }}>
              {theme.mentions} mention{theme.mentions === 1 ? "" : "s"}
            </Text>
          </Flex>
          {index < themes.length - 1 && <Separator size="4" />}
        </Box>
      )) : <Text size="2" color="gray">No {sentiment} themes detected for this selection.</Text>}
    </Flex>
  );
}

export default function ReviewsDashboard() {
  const { userProfile } = useProfileContext();
  const { locationSelection } = useLocationContext();
  const { selectedDateRange } = useDateRangeContext();
  const { sentimentFilter, ratingFilter, repliedFilter, sourceFilter } = useReviewsFilterContext();

  const selectedLocationIds = useMemo(
    () => (locationSelection || []).map(Number).filter(Boolean),
    [locationSelection],
  );
  const params = useMemo(() => {
    const sentiments = sentimentFilter.length === SENTIMENT_OPTIONS.length ? [] : sentimentFilter;
    const ratings = ratingFilter.length === RATING_OPTIONS.length ? [] : ratingFilter.map(Number);
    const replied = repliedFilter.length === 1 ? repliedFilter[0] === "true" : undefined;
    return {
      organisationId: userProfile?.organisation_id,
      locationIds: selectedLocationIds,
      startDate: selectedDateRange?.from,
      endDate: selectedDateRange?.to,
      sentiments,
      ratings,
      sources: sourceFilter?.length ? sourceFilter : [],
      replied,
    };
  }, [userProfile?.organisation_id, selectedLocationIds, selectedDateRange, sentimentFilter, ratingFilter, repliedFilter, sourceFilter]);

  const { data: statsData, isLoading, isError } = useGetDashboardStats(params);
  const { data: weeklySummaryData, isLoading: isWeeklyLoading } = useGetWeeklySummary({ organisationId: userProfile?.organisation_id });
  const stats = statsData?.data?.stats || {};
  const weeklySummary = weeklySummaryData?.data?.summary;
  const sentiment = useMemo(() => ({
    positive: stats.sentiment_breakdown?.positive || 0,
    neutral: stats.sentiment_breakdown?.neutral || 0,
    negative: stats.sentiment_breakdown?.negative || 0,
  }), [stats.sentiment_breakdown]);

  if (isLoading || isWeeklyLoading) return <Progress size="1" duration="1" width="100%" />;
  if (isError) return <Flex align="center" justify="center" width="100%" height="100%" p="8"><Text color="red">Error loading dashboard data</Text></Flex>;

  return (
    <ScrollArea type="auto" scrollbars="vertical" style={{ width: "100%", height: "100%" }}>
      <Flex direction="column" gap="5" width="100%" pb="6" pr="2">
        <Callout.Root color="orange" variant="outline" size="2" style={{ background: 'var(--color-panel-solid)' }}>
          <Callout.Text style={{ color: 'var(--orange-12)' }}>
            <Flex direction="column" gap="2">
              <Heading as="h2" size="4" color="orange">Last Week&apos;s AI Summary</Heading>
              {weeklySummary ? <>
                <Text size="2" style={{ whiteSpace: "pre-wrap" }}>{weeklySummary.summary_text || "Summary unavailable."}</Text>
                <Text size="1" color="gray">Week of {formatISODate(weeklySummary.week_start)} to {formatISODate(weeklySummary.week_end)}</Text>
              </> : <Text size="2" color="gray">No weekly summary available yet. Try sending an email to your customers to get reviews!</Text>}
            </Flex>
          </Callout.Text>
        </Callout.Root>

        <Card size="2">
          <Flex direction="column" gap="2">
            <Text size="2" weight="medium">Sentiment Analysis</Text>
            <StackedStatusBar stackedStatusBarData={sentiment} />
          </Flex>
        </Card>

        <Grid columns={{ initial: "1", md: "2" }} gap="5" width="100%">
          <ChartCard text="Reviews by Rating"><ReviewsByRatingChart data={stats.rating_breakdown} /></ChartCard>
          <ChartCard text="Sentiment Trend"><SentimentTrendChart data={stats.sentiment_over_time} /></ChartCard>
        </Grid>

        <Card size="3">
          <Flex direction="column" gap="5">
            <Flex direction="column" gap="1">
              <Heading as="h2" size="5">What customers are talking about</Heading>
              <Text size="2" color="gray">Themes detected from reviews in the selected period</Text>
            </Flex>
            <Grid columns={{ initial: "1", md: "2" }} gap="7">
              <ThemeColumn sentiment="positive" themes={stats.themes?.positive || []} />
              <ThemeColumn sentiment="negative" themes={stats.themes?.negative || []} />
            </Grid>
          </Flex>
        </Card>
      </Flex>
    </ScrollArea>
  );
}
